import { useState } from "react";
import { TrashIcon } from "@heroicons/react/24/solid";
import ConfirmDelete from "./confirm-item-delete";
import { ICartProduct } from "utils/types";
import { useAppDispatch } from "@store/store";
import { removeProductFromCart, editCartProduct } from "@store/slice/cart-slice";

interface CartItemProps {
  product: ICartProduct;
}

const CartItem = ({ product }: CartItemProps) => {
  const dispatch = useAppDispatch();
  const [newQuantity, setNewQuantity] = useState(product.productQty);
  const [openModal, setOpenModal] = useState(false);

  const inc = () => {
    const next = newQuantity + 1;
    if (product.availableQty && next > product.availableQty) return;
    setNewQuantity(next);
    dispatch(editCartProduct({ ...product, productQty: next }));
  };

  const dec = () => {
    if (newQuantity > 1) {
      const next = newQuantity - 1;
      setNewQuantity(next);
      dispatch(editCartProduct({ ...product, productQty: next }));
    } else {
      setOpenModal(true);
    }
  };

  const removeItem = () => {
    dispatch(removeProductFromCart(product));
    setOpenModal(false);
  };

  const confirmChild = (
    <div className="flex-1 p-2 bg-gray-100 my-2 rounded-md">
      <div className="flex items-start">
        <div className="flex-shrink-0 pt-0.5">
          <img
            className="h-12 w-12 rounded-md object-cover"
            src={product.imageLink}
            alt={product.name ?? ""}
          />
        </div>
        <div className="ml-6 flex-1 flex flex-col">
          <span className="text-xs font-medium text-gray-500">
            {product.name}
          </span>
          <span className="mt-1 text-sm text-gray-900">
            {product.currency} {Number(product.productPrice ?? 0).toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="flex items-center justify-between bg-white rounded-lg shadow-sm m-6 p-2">
        <div className="flex-shrink-0">
          <img
            src={product.imageLink}
            alt={product.name ?? ""}
            className="h-16 w-16 rounded-md object-cover"
          />
        </div>

        <div className="flex-1 ml-3">
          <h3 className="font-semibold text-gray-800">{product.name}</h3>
          <p className="text-sm text-gray-500">
            SKU: <span className="text-gray-600">{product.productSKU}</span>
          </p>
          <p className="text-gray-800 font-medium mt-2">
            {product.currency} {Number(product.productPrice ?? 0).toFixed(2)} x{" "}
            {newQuantity}
          </p>
        </div>

        <div className="mr-3">
          <button
            type="button"
            onClick={() => setOpenModal(true)}
            className="p-2 text-red-500 hover:bg-red-100 rounded-full"
            aria-label="Delete item"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={dec}
            className="flex items-center justify-center h-8 w-8 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100"
          >
            –
          </button>
          <span className="w-4 text-center font-medium text-gray-800">
            {newQuantity}
          </span>
          <button
            type="button"
            onClick={inc}
            className="flex items-center justify-center h-8 w-8 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100"
          >
            +
          </button>
        </div>
      </div>

      <ConfirmDelete
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        onConfirm={removeItem}
        child={confirmChild}
      />
    </>
  );
};

export default CartItem;
